import { h } from "@stencil/core";
import { ICON_NAMESPACE } from "../../icons/namespace";
/** A button with an icon that displays the state of a async action on click. */
export class YThinkingButton {
  constructor() {
    this.iconName = (status) => {
      switch (status) {
        case 'thinking':
          return this.thinkingIcon;
        case 'complete':
          return this.completeIcon;
        case 'failed':
          return this.failedIcon;
        default:
          return this.defaultIcon;
      }
    };
    this.execute = async (e) => {
      clearTimeout(this.timeout);
      this.status = 'thinking';
      try {
        await this.action(e);
        this.status = 'complete';
      }
      catch (error) {
        this.status = 'failed';
      }
      this.timeout = setTimeout(() => {
        this.status = 'default';
      }, 2000);
    };
    this.action = undefined;
    this.text = undefined;
    this.variant = 'default';
    this.disabled = undefined;
    this.defaultIcon = undefined;
    this.thinkingIcon = [ICON_NAMESPACE, 'spinner'];
    this.completeIcon = [ICON_NAMESPACE, 'check'];
    this.failedIcon = [ICON_NAMESPACE, 'error'];
    this.status = 'default';
  }
  disconnectedCallback() {
    clearTimeout(this.timeout);
  }
  render() {
    return (h("es-button", { variant: this.variant, onClick: this.execute, disabled: this.disabled }, this.text, h("es-icon", { slot: this.text ? 'after' : undefined, class: this.status, icon: this.iconName(this.status) })));
  }
  static get is() { return "es-thinking-button"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() {
    return {
      "$": ["es-thinking-button.css"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["es-thinking-button.css"]
    };
  }
  static get properties() {
    return {
      "action": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(e: MouseEvent) => Promise<unknown>",
          "resolved": "(e: MouseEvent) => Promise<unknown>",
          "references": {
            "MouseEvent": {
              "location": "global",
              "id": "global::MouseEvent"
            },
            "Promise": {
              "location": "global",
              "id": "global::Promise"
            }
          }
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "The async action to be called on click."
        }
      },
      "text": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Optional text of the button"
        },
        "attribute": "text",
        "reflect": false
      },
      "variant": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "HTMLEsButtonElement['variant']",
          "resolved": "\"cancel\" | \"default\" | \"delete\" | \"filled\" | \"link\" | \"minimal\" | \"outline\"",
          "references": {
            "HTMLEsButtonElement": {
              "location": "global",
              "id": "global::HTMLEsButtonElement"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Which styling variant to use."
        },
        "attribute": "variant",
        "reflect": false,
        "defaultValue": "'default'"
      },
      "disabled": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "If the button is disabled. Prevents the user from interacting with the button: it cannot be pressed or focused."
        },
        "attribute": "disabled",
        "reflect": false
      },
      "defaultIcon": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "IconDescription",
          "resolved": "[namespace: string | symbol, name: string] | string",
          "references": {
            "IconDescription": {
              "location": "import",
              "path": "../../components/es-icon/types",
              "id": "src/components/es-icon/types.ts::IconDescription"
            }
          }
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "The default icon to display when nothing is happening."
        },
        "attribute": "default-icon",
        "reflect": false
      },
      "thinkingIcon": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "IconDescription",
          "resolved": "[namespace: string | symbol, name: string] | string",
          "references": {
            "IconDescription": {
              "location": "import",
              "path": "../../components/es-icon/types",
              "id": "src/components/es-icon/types.ts::IconDescription"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "The icon to display when we are awaiting the action."
        },
        "attribute": "thinking-icon",
        "reflect": false,
        "defaultValue": "[ICON_NAMESPACE, 'spinner']"
      },
      "completeIcon": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "IconDescription",
          "resolved": "[namespace: string | symbol, name: string] | string",
          "references": {
            "IconDescription": {
              "location": "import",
              "path": "../../components/es-icon/types",
              "id": "src/components/es-icon/types.ts::IconDescription"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "The icon to display when the action completed successfully."
        },
        "attribute": "complete-icon",
        "reflect": false,
        "defaultValue": "[ICON_NAMESPACE, 'check']"
      },
      "failedIcon": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "IconDescription",
          "resolved": "[namespace: string | symbol, name: string] | string",
          "references": {
            "IconDescription": {
              "location": "import",
              "path": "../../components/es-icon/types",
              "id": "src/components/es-icon/types.ts::IconDescription"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "The icon to display when the action errored out."
        },
        "attribute": "failed-icon",
        "reflect": false,
        "defaultValue": "[ICON_NAMESPACE, 'error']"
      }
    };
  }
  static get states() {
    return {
      "status": {}
    };
  }
}
//# sourceMappingURL=es-thinking-button.js.map
